import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Check } from "@phosphor-icons/react";
import { THEMES, AMENITY_SUGGESTIONS } from "@/lib/data";
import { F, TextF, Group, ImageField, RowList, ChipList } from "./fields";
import { PlaceList, WaAlertLog } from "./GuideParts";

const Toggle = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl bg-secondary px-4 py-3 sm:col-span-2">
    <div><p className="text-sm font-semibold">{label}</p>{desc && <p className="text-xs text-muted-foreground">{desc}</p>}</div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export function HostSection({ d, up }) {
  const h = d.host;
  const s = (k) => (v) => up((x) => { x.host[k] = v; });
  return (
    <Group title="Host profile" desc="Guests see this card with call and WhatsApp buttons.">
      <TextF label="Your name" value={h.name} onChange={s("name")} testId="host-name-input" />
      <ImageField label="Your photo" value={h.photo} onChange={s("photo")} testId="host-photo" round />
      <ImageField label="Your logo (cards, invoices)" value={h.logo} onChange={s("logo")} testId="host-logo" />
      <TextF label="Short bio" value={h.bio} onChange={s("bio")} testId="host-bio-input" area className="sm:col-span-2" />
      <TextF label="Phone" value={h.phone} onChange={s("phone")} testId="host-phone-input" placeholder="+256 7…" />
      <TextF label="WhatsApp" value={h.whatsapp} onChange={s("whatsapp")} testId="host-whatsapp-input" placeholder="+256 7…" />
      <TextF label="Instagram link" value={h.instagram} onChange={s("instagram")} testId="host-instagram-input" placeholder="https://instagram.com/…" />
      <TextF label="Facebook link" value={h.facebook} onChange={s("facebook")} testId="host-facebook-input" />
      <TextF label="TikTok link" value={h.tiktok} onChange={s("tiktok")} testId="host-tiktok-input" />
      <TextF label="Website" value={h.website} onChange={s("website")} testId="host-website-input" />
    </Group>
  );
}

export function PropertySection({ d, up }) {
  const s = (k) => (v) => up((x) => { x[k] = v; });
  return (
    <Group title="Property" desc="Address and directions stay hidden until the guest checks in.">
      <TextF label="Property name" value={d.name} onChange={s("name")} testId="property-name-input" className="sm:col-span-2" />
      <TextF label="Description" value={d.description} onChange={s("description")} testId="property-description-input" area className="sm:col-span-2" />
      <TextF label="Address" value={d.address} onChange={s("address")} testId="property-address-input" className="sm:col-span-2" />
      <TextF label="Directions" value={d.directions} onChange={s("directions")} testId="property-directions-input" area className="sm:col-span-2" />
      <TextF label="Google Maps link (optional)" hint="If empty, we search the address in Maps." value={d.map_url} onChange={s("map_url")} testId="property-mapurl-input" className="sm:col-span-2" />
      <ImageField label="Cover photo" value={d.cover} onChange={s("cover")} testId="property-cover" />
      <ImageField label="Background picture" value={d.background} onChange={s("background")} testId="property-background" />
    </Group>
  );
}

export function StaySection({ d, up }) {
  const s = (k) => (v) => up((x) => { x[k] = v; });
  return (
    <Group title="Stay details" desc="Unlocked after check-in.">
      <TextF label="Check-in from" type="time" value={d.checkin_time} onChange={s("checkin_time")} testId="stay-checkin-time" />
      <TextF label="Check-out by" type="time" value={d.checkout_time} onChange={s("checkout_time")} testId="stay-checkout-time" />
      <TextF label="Wi-Fi name" value={d.wifi_name} onChange={s("wifi_name")} testId="stay-wifi-name" />
      <TextF label="Wi-Fi password" value={d.wifi_password} onChange={s("wifi_password")} testId="stay-wifi-password" />
      <F label="Amenities" className="sm:col-span-2"><ChipList items={d.amenities} onChange={s("amenities")} suggestions={AMENITY_SUGGESTIONS} testId="amenities" placeholder="Add an amenity" /></F>
    </Group>
  );
}

export function RatesSection({ d, up }) {
  const r = d.rates;
  const s = (k) => (v) => up((x) => { x.rates[k] = v; });
  return (
    <Group title="Rates" desc="Optional. Show your direct-booking prices on the page.">
      <Toggle label="Show rates on the guest page" checked={r.show} onChange={s("show")} testId="rates-show-switch" />
      <TextF label="Currency" value={r.currency} onChange={s("currency")} testId="rates-currency-input" placeholder="UGX, KES, TZS, RWF, USD" />
      <TextF label="Per night" inputMode="numeric" value={r.nightly} onChange={s("nightly")} testId="rates-nightly-input" />
      <TextF label="Weekend night" inputMode="numeric" value={r.weekend} onChange={s("weekend")} testId="rates-weekend-input" />
      <TextF label="Per week" inputMode="numeric" value={r.weekly} onChange={s("weekly")} testId="rates-weekly-input" />
      <TextF label="Per month" inputMode="numeric" value={r.monthly} onChange={s("monthly")} testId="rates-monthly-input" />
      <TextF label="Cleaning fee" inputMode="numeric" value={r.cleaning_fee} onChange={s("cleaning_fee")} testId="rates-cleaning-input" />
      <TextF label="Deposit" inputMode="numeric" value={r.deposit} onChange={s("deposit")} testId="rates-deposit-input" />
      <F label="Extras" className="sm:col-span-2"><RowList items={r.extras} onChange={s("extras")} fields={[["label", "e.g. Airport pickup"], ["amount", "Amount"]]} addLabel="Add an extra" testId="rates-extras" /></F>
    </Group>
  );
}

export function OfferSection({ d, up }) {
  const o = d.offer;
  const s = (k) => (v) => up((x) => { x.offer[k] = v; });
  return (
    <Group title="Offer banner" desc="Bring guests back by booking directly.">
      <Toggle label="Show the offer banner" checked={o.enabled} onChange={s("enabled")} testId="offer-enabled-switch" />
      <TextF label="Badge" value={o.badge} onChange={s("badge")} testId="offer-badge-input" placeholder="Returning guest" />
      <TextF label="Promo code" value={o.code} onChange={s("code")} testId="offer-code-input" placeholder="KARIBU10" />
      <TextF label="Headline" value={o.headline} onChange={s("headline")} testId="offer-headline-input" className="sm:col-span-2" />
      <TextF label="Ends on" type="date" value={o.ends} onChange={s("ends")} testId="offer-ends-input" />
    </Group>
  );
}

export function GuideSection({ d, up }) {
  const s = (k) => (v) => up((x) => { x[k] = v; });
  return (
    <Group title="Guide" desc="House rules, nearby places and emergency contacts.">
      <F label="House rules (one per line)" className="sm:col-span-2">
        <Textarea rows={5} data-testid="guide-rules-input" value={d.rules.join("\n")} onChange={(e) => s("rules")(e.target.value.split("\n"))} onBlur={() => s("rules")(d.rules.map((r) => r.trim()).filter(Boolean))} />
      </F>
      <F label="Favourite spots" hint="Pick a category to pin a place under that Find nearby button. General places show in the Nearby list." className="sm:col-span-2"><PlaceList items={d.nearby} onChange={s("nearby")} /></F>
      <F label="Emergency contacts" className="sm:col-span-2"><RowList items={d.emergency} onChange={s("emergency")} fields={[["label", "Who"], ["phone", "Phone"]]} addLabel="Add a contact" testId="guide-emergency" /></F>
      <TextF label="Google review link" value={d.review_url} onChange={s("review_url")} testId="guide-review-input" className="sm:col-span-2" placeholder="https://g.page/r/…" />
    </Group>
  );
}

export function FormSection({ d, up, property }) {
  const f = d.form;
  const s = (k) => (v) => up((x) => { x.form[k] = v; });
  return (
    <Group title="Check-in form" desc="Guests fill this in before house info unlocks. Each form is emailed to you as a PDF.">
      <Toggle label="Ask guests to check in" desc="If off, house info shows straight away." checked={f.enabled} onChange={s("enabled")} testId="form-enabled-switch" />
      <Toggle label="Thank-you email after check-out" desc="Guests who gave an email get a branded thank-you with their receipt and feedback link." checked={f.thank_you !== false} onChange={s("thank_you")} testId="form-thankyou-switch" />
      <Toggle label="WhatsApp alert on each check-in" desc="Get a WhatsApp message with the guest's name, dates and phone the moment they check in." checked={!!f.wa_alerts} onChange={s("wa_alerts")} testId="form-wa-switch" />
      {f.wa_alerts && <TextF label="Your WhatsApp number" placeholder="+256 772 123 456" value={f.wa_number || ""} onChange={s("wa_number")} testId="form-wa-number-input" className="sm:col-span-2" />}
      {f.wa_alerts && <WaAlertLog pid={property?.property_id} />}
      <TextF label="Send forms to" type="email" value={f.email} onChange={s("email")} testId="form-email-input" />
      <TextF label="Business name (privacy note)" value={f.business} onChange={s("business")} testId="form-business-input" />
      <TextF label="Ask for ID from this many nights" hint="Set 0 to never ask for an ID." type="number" min={0} value={f.id_nights} onChange={(v) => s("id_nights")(Math.max(0, parseInt(v || "0", 10)))} testId="form-idnights-input" />
      <TextF label="Welcome message" value={f.welcome} onChange={s("welcome")} testId="form-welcome-input" area className="sm:col-span-2" />
    </Group>
  );
}

export function ThemeSection({ d, up }) {
  return (
    <section className="rounded-2xl border border-border bg-white p-6">
      <h3 className="font-display text-lg">Guest page theme</h3>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {THEMES.map((t) => (
          <button key={t.id} type="button" onClick={() => up((x) => { x.theme = t.id; })} data-testid={`theme-option-${t.id}`}
            className={`relative rounded-xl border-2 p-4 text-left transition-colors duration-200 ${d.theme === t.id ? "border-[#D96C4A]" : "border-border hover:border-[#151312]/40"}`}>
            <div className="flex h-16 overflow-hidden rounded-lg">{t.swatch.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}</div>
            <p className="mt-3 font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{t.note}</p>
            {d.theme === t.id && <span className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-full bg-[#D96C4A] text-white"><Check size={14} weight="bold" /></span>}
          </button>
        ))}
      </div>
    </section>
  );
}
